'use client';
// Liens typés entre entités qualité — composant UNIQUE, réutilisable tel quel
// par n'importe quelle fiche (NC, action, document, équipement, lot…).
//
// Huit types d'entités font 56 paires possibles : un écran par paire serait
// ingérable. D'où un seul composant, paramétré par l'entité courante, et un
// sélecteur en deux temps (type d'entité, puis objet de ce type). Jamais de
// saisie libre — c'est elle qui produisait des rattachements pointant dans le
// vide.
import React, { useMemo, useState } from 'react';
import { useGed, type TypePanneau } from '@/lib/store';
import { TYPES_LIEN, ENTITES_LIABLES } from '@/lib/meta';
import { I, inputStyle, ConfirmButton } from '@/components/ui';
import type { GedLien, GedView } from '@/lib/types';

import { Bouton } from '@/shared/Bouton';
import { useProduits, libelleProduit } from '@/components/ChoixProduit';
/** Objets sélectionnables pour chaque type d'entité, avec leur libellé. */
function useCatalogue() {
  const { ncs, actions, docs, equips, lots, recls, audits, fourns, eeqs, rdds, analytes } = useGed();
  // Le catalogue du matériel (16/10/2026) : l'article avant le lot.
  const { lignes: produits } = useProduits();
  return useMemo(() => ({
    produit: produits.map((x) => ({ ref: x.code, label: libelleProduit(x) })),
    nc: ncs.map((x) => ({ ref: x.ref, label: x.titre })),
    action: actions.map((x) => ({ ref: x.ref, label: x.titre })),
    document: docs.map((x) => ({ ref: x.code, label: x.titre })),
    // La référence lisible d'un équipement est son `code` d'inventaire (EQ09),
    // et celle d'un fournisseur son `nom` : ni l'un ni l'autre ne porte de
    // champ `ref`. Les lire sous ce nom rendait les deux listes vides — donc
    // impossible de rattacher une NC à l'automate qui l'a produite.
    equipement: equips.map((x) => ({ ref: x.code || '', label: x.nom || '' })),
    lot: lots.map((x) => ({ ref: (x as { ref?: string }).ref || '', label: (x as { designation?: string }).designation || '' })),
    reclamation: recls.map((x) => ({ ref: x.ref, label: x.objet })),
    audit: audits.map((x) => ({ ref: x.ref, label: (x as { theme?: string }).theme || x.ref })),
    fournisseur: fourns.map((x) => ({ ref: x.nom || '', label: x.domaine || x.fournType || '' })),
    analyte: analytes.map((x) => ({ ref: x.code, label: `${x.nom}${x.discipline ? ` — ${x.discipline}` : ''}` })),
    eeq: eeqs.map((x) => ({ ref: x.ref || '', label: `${x.analyte} · ${x.programme}${x.zscore != null ? ` · z=${x.zscore}` : ''}` })),
    rdd: rdds.map((x) => ({ ref: x.ref, label: x.dateRevue })),
  } as Record<string, { ref: string; label: string }[]>), [ncs, actions, docs, equips, lots, recls, audits, fourns, eeqs, rdds, analytes, produits]);
}

/**
 * @param type  type de l'entité courante (`nc`, `action`, `document`…)
 * @param refCourante référence lisible de l'entité courante
 */
export function Liens({ type, refCourante }: { type: string; refCourante: string }) {
  const { liens, creerLien, retirerLien, session, ouvrirDepuis, ouvrirPanneau, ncs, actions, equips, recls, audits, fourns, rdds } = useGed();
  const catalogue = useCatalogue();
  const [ouvert, setOuvert] = useState(false);
  const [cibleType, setCibleType] = useState('document');
  const [cibleRef, setCibleRef] = useState('');
  const [typeLien, setTypeLien] = useState('concerne');
  const [q, setQ] = useState('');
  const estRQ = session.roles.includes('rq');

  // Les DEUX sens. C'est là qu'est l'essentiel de la valeur : ouvrir un lot et
  // voir les non-conformités qu'il a provoquées, c'est ça, l'outil qualité.
  const sortants = liens.filter((l) => l.sourceType === type && l.sourceRef === refCourante);
  const entrants = liens.filter((l) => l.cibleType === type && l.cibleRef === refCourante);

  const options = (catalogue[cibleType] || [])
    .filter((o) => o.ref && !(cibleType === type && o.ref === refCourante))
    .filter((o) => !q.trim() || `${o.ref} ${o.label}`.toLowerCase().includes(q.trim().toLowerCase()));

  /** Types d'objet qui ont un aperçu en panneau. */
  const PANNEAU: Record<string, TypePanneau> = {
    nc: 'nc', action: 'action', document: 'doc', equipement: 'equip',
    reclamation: 'recl', audit: 'audit',
  };

  // Suivre un lien, c'est CONSULTER un objet lié — pas quitter la fiche qu'on
  // lit (décision du 12/08/2026). Il s'ouvre donc EN PANNEAU par-dessus, et la
  // fiche courante reste ouverte dessous avec son onglet, son filtre et sa
  // position : plus rien n'est à restaurer, puisque rien n'est quitté. Ça
  // corrige du même geste le défaut le plus visible — pour un document, une
  // réclamation ou un audit, aucune clé de sélection n'était passée et on
  // atterrissait sur le REGISTRE, affichant potentiellement une autre fiche.
  const ouvrir = (t: string, ref: string) => {
    // Le document et l'équipement se désignent par leur référence lisible
    // (code) ; les autres objets par leur `documentId`.
    const valeur = t === 'nc' ? ncs.find((n) => n.ref === ref)?.documentId || null
      : t === 'action' ? actions.find((a) => a.ref === ref)?.documentId || null
        : t === 'equipement' ? ref
          : t === 'document' ? ref
            : t === 'reclamation' ? recls.find((r) => r.ref === ref)?.documentId || null
              : t === 'audit' ? audits.find((a) => a.ref === ref)?.documentId || null
                : t === 'fournisseur' ? fourns.find((f) => f.nom === ref)?.documentId || null
                  : t === 'rdd' ? rdds.find((r) => r.ref === ref)?.documentId || null
                    : null;
    const panneau = PANNEAU[t];
    if (panneau && valeur) { ouvrirPanneau({ type: panneau, valeur, origine: refCourante }); return; }
    // Pas d'aperçu pour ce type (lot, fournisseur, analyte, EEQ, revue de
    // direction) : on garde la vraie navigation, en désignant l'objet quand
    // une clé de sélection existe pour lui.
    const vue = ENTITES_LIABLES[t]?.vue;
    if (!vue) return;
    const cle = t === 'nc' ? 'selectedNC' : t === 'action' ? 'selectedAction' : t === 'equipement' ? 'selectedEquip'
      : t === 'fournisseur' ? 'selectedFourn' : t === 'rdd' ? 'selectedRdd' : undefined;
    ouvrirDepuis(refCourante, { view: vue as GedView, cle, valeur });
  };

  const Ligne = ({ l, sens }: { l: GedLien; sens: 'sortant' | 'entrant' }) => {
    const autreType = sens === 'sortant' ? l.cibleType : l.sourceType;
    const autreRef = sens === 'sortant' ? l.cibleRef : l.sourceRef;
    const meta = TYPES_LIEN[l.typeLien];
    const verbe = sens === 'sortant' ? (meta?.label || l.typeLien) : (meta?.inverse || l.typeLien);
    const nom = (catalogue[autreType] || []).find((o) => o.ref === autreRef)?.label;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 0', borderTop: '1px solid var(--border-subtle)' }}>
        <span title={meta?.aide} style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', cursor: meta?.aide ? 'help' : 'default', minWidth: 92 }}>{verbe}</span>
        <span style={{ fontSize: 12.5, color: 'var(--text-faint, var(--text-muted))' }}>{ENTITES_LIABLES[autreType]?.label || autreType}</span>
        <Bouton variante="primaire" taille="sm" onClick={() => ouvrir(autreType, autreRef)} title={`Ouvrir ${autreRef}`} style={{ fontFamily: 'var(--font-mono)' }}>{autreRef} →</Bouton>
        {nom && <span style={{ fontSize: 12.5, color: 'var(--text-body)', flex: '1 1 140px', minWidth: 0 }}>{nom}</span>}
        {/* Un lien retiré ne réécrit pas l'histoire : l'acte est tracé au journal
            des deux objets. Réservé au RQ. */}
        {estRQ && sens === 'sortant' && (
          <ConfirmButton
            label="" titre="Retirer ce lien" icon="trash" variant="outline"
            question={`Retirer le lien vers ${autreRef} ?`} confirmer="Retirer"
            onConfirm={() => retirerLien(l.documentId)} />
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>
          Liens — {sortants.length + entrants.length}
        </span>
        <span style={{ flex: 1 }} />
        {!ouvert && (
          <Bouton taille="sm" onClick={() => { setOuvert(true); setQ(''); setCibleRef(''); }}>
            <I n="plus" s={13} />Lier…
          </Bouton>
        )}
      </div>

      {sortants.length + entrants.length === 0 && !ouvert && (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
          Aucun objet rattaché. « Lier… » rattache un document, un équipement, un réactif ou une autre fiche qualité.
        </p>
      )}
      {sortants.map((l) => <Ligne key={l.documentId} l={l} sens="sortant" />)}
      {entrants.map((l) => <Ligne key={l.documentId} l={l} sens="entrant" />)}

      {ouvert && (
        <div style={{ display: 'grid', gap: 8, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select value={typeLien} onChange={(e) => setTypeLien(e.target.value)} title={TYPES_LIEN[typeLien]?.aide}
              aria-label="Nature du lien" style={{ ...inputStyle, width: 'auto', minWidth: 150 }}>
              {Object.entries(TYPES_LIEN).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={cibleType} onChange={(e) => { setCibleType(e.target.value); setCibleRef(''); setQ(''); }}
              aria-label="Type d’objet à lier" style={{ ...inputStyle, width: 'auto', minWidth: 170 }}>
              {Object.entries(ENTITES_LIABLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par référence ou intitulé…"
            aria-label="Rechercher l’objet à lier" style={inputStyle} />
          <select value={cibleRef} onChange={(e) => setCibleRef(e.target.value)} size={5}
            aria-label="Objet à lier" style={{ ...inputStyle, height: 'auto', padding: 6 }}>
            {options.length === 0 && <option value="" disabled>Aucun objet de ce type</option>}
            {options.slice(0, 50).map((o) => <option key={o.ref} value={o.ref}>{o.ref} — {o.label}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Bouton variante="primaire" taille="sm" onClick={() => void creerLien({ sourceType: type, sourceRef: refCourante, cibleType, cibleRef, typeLien }).then((ok) => { if (ok) { setOuvert(false); setCibleRef(''); } })} disabled={!cibleRef}>
              <I n="check" s={13} />Lier
            </Bouton>
            <Bouton taille="sm" onClick={() => setOuvert(false)}>Annuler</Bouton>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{TYPES_LIEN[typeLien]?.aide}</span>
          </div>
        </div>
      )}
    </div>
  );
}
